/**
 * Client Domain Schemas
 * Validation schemas for client entities and operations
 */

import { z } from 'zod';
import { uuidSchema, emailSchema, dateStringSchema, urlSchema } from './common.schemas';

/**
 * Client Type Enum
 */
export const clientTypeSchema = z.enum(['individual', 'business']);
export type ClientType = z.infer<typeof clientTypeSchema>;

/**
 * Client Status Enum
 */
export const clientStatusSchema = z.enum(['active', 'inactive', 'archived', 'lead']);
export type ClientStatus = z.infer<typeof clientStatusSchema>;

/**
 * Base Client Schema
 * Represents a complete client record from the database
 */
export const clientSchema = z.object({
  id: uuidSchema,
  user_id: uuidSchema,
  organization_id: uuidSchema.nullable(),
  name: z.string().min(1).max(200),
  email: emailSchema.nullable(),
  phone: z.string().max(50).nullable(),
  company: z.string().max(200).nullable(),
  type: clientTypeSchema.default('individual'),
  status: clientStatusSchema.default('active'),
  address_line1: z.string().max(200).nullable(),
  address_line2: z.string().max(200).nullable(),
  city: z.string().max(100).nullable(),
  state: z.string().max(100).nullable(),
  postcode: z.string().max(20).nullable(),
  country: z.string().max(100).default('Australia'),
  website: urlSchema.nullable(),
  notes: z.string().max(5000).nullable(),
  tags: z.array(z.string()).default([]),
  custom_fields: z.record(z.unknown()).default({}),
  created_at: dateStringSchema,
  updated_at: dateStringSchema,
});

export type Client = z.infer<typeof clientSchema>;

/**
 * Create Client Schema
 * For creating new clients
 */
export const createClientSchema = clientSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).extend({
  user_id: uuidSchema.optional(),
  organization_id: uuidSchema.optional(),
  name: z.string().min(1, 'Name is required').max(200, 'Name must be less than 200 characters').trim(),
  email: emailSchema.optional().or(z.literal('')).nullable().transform(val => val || null),
  phone: z.string().max(50).optional().nullable().transform(val => val || null),
  website: z.string().url().optional().or(z.literal('')).nullable().transform(val => val || null),
});

export type CreateClientInput = z.infer<typeof createClientSchema>;

/**
 * Update Client Schema
 * For partial updates
 */
export const updateClientSchema = clientSchema.partial().required({
  id: true,
}).omit({
  user_id: true,
  created_at: true,
});

export type UpdateClientInput = z.infer<typeof updateClientSchema>;

/**
 * Client Filter Schema
 * For querying clients
 */
export const clientFilterSchema = z.object({
  type: z.array(clientTypeSchema).optional(),
  status: z.array(clientStatusSchema).optional(),
  organization_id: uuidSchema.optional(),
  tags: z.array(z.string()).optional(),
  search: z.string().max(500).optional(),
  created_after: dateStringSchema.optional(),
  created_before: dateStringSchema.optional(),
  has_email: z.boolean().optional(),
  has_phone: z.boolean().optional(),
});

export type ClientFilter = z.infer<typeof clientFilterSchema>;

/**
 * Client Contact Schema
 * For additional contact persons
 */
export const clientContactSchema = z.object({
  id: uuidSchema,
  client_id: uuidSchema,
  name: z.string().min(1).max(200),
  email: emailSchema.nullable(),
  phone: z.string().max(50).nullable(),
  position: z.string().max(100).nullable(),
  is_primary: z.boolean().default(false),
  notes: z.string().max(1000).nullable(),
  created_at: dateStringSchema,
  updated_at: dateStringSchema,
});

export type ClientContact = z.infer<typeof clientContactSchema>;

/**
 * Create Client Contact Schema
 */
export const createClientContactSchema = clientContactSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).extend({
  name: z.string().min(1, 'Name is required').max(200, 'Name must be less than 200 characters').trim(),
  email: emailSchema.optional().or(z.literal('')).nullable().transform(val => val || null),
  phone: z.string().max(50).optional().nullable().transform(val => val || null),
});

export type CreateClientContactInput = z.infer<typeof createClientContactSchema>;

/**
 * Client Import Schema
 * For bulk client import
 */
export const clientImportSchema = z.object({
  name: z.string().min(1, 'Name is required').trim(),
  email: emailSchema.optional(),
  phone: z.string().optional(),
  company: z.string().optional(),
  type: clientTypeSchema.optional(),
  notes: z.string().optional(),
});

export type ClientImportRow = z.infer<typeof clientImportSchema>;
